/**
 * Highlighting and notes, over text the platform rendered.
 *
 * A mark is stored as a pair of offsets into the container's plain text, not
 * as a wrapper element. The question pane re-renders constantly — an answer
 * typed, a part changed, the contrast switched — and anything written into the
 * DOM is thrown away each time. Offsets survive that: they are re-applied
 * after every render, and they are what a highlight actually is, which is a
 * span of the candidate's own reading.
 */

export interface Mark {
  id: string;
  start: number;
  end: number;
  /** A note's text. Absent on a plain highlight. */
  note?: string;
}

/**
 * The text a candidate may mark: only inside `[data-markable]`.
 *
 * Those are the blocks the platform renders as opaque HTML — the passage, a
 * body of notes, a question's own prompt. Everything else on the screen is
 * React's, down to the individual text node, and wrapping one of those in a
 * <mark> is a change React does not know it has: the next update either
 * throws it away or trips over it. So the ground a highlight may cover is
 * exactly the ground React is not managing character by character.
 */
/**
 * What sits inside the paper's text but is not the paper: the answer controls.
 *
 * A gap in a summary or a set of notes holds a control — a typed box with its
 * number, a drop zone reading "Drop answer here" until a card lands on it, a
 * dropdown of letters — and a passage with a headings task has a drop slot
 * above each paragraph. Their text is the candidate's answering, and it
 * changes as they answer.
 *
 * It used to be counted along with the paper, so every answer slid every
 * highlight after it by the difference in length. Drop "shelf" into gap 14 and
 * a highlight on "out of reach" became "of reach." plus a stray "Do " in the
 * next question's instructions — and stayed broken after moving between parts,
 * because the marks were stored in the shifted positions. To a candidate the
 * highlight had simply gone wrong or gone.
 */
const ANSWERING = '[data-gap], .passage__slot, .xgap, .xdrop, input, select, option, textarea, button';

function textNodes(root: Node): Text[] {
  const out: Text[] = [];
  const regions =
    root instanceof Element && root.matches('[data-markable]')
      ? [root]
      : [...(root as Element).querySelectorAll?.('[data-markable]') ?? []];

  for (const region of regions) {
    const walker = document.createTreeWalker(region, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      // Only a control inside this region counts: an ancestor outside it is
      // the page around the paper, not an answer box within it.
      const control = node.parentElement?.closest(ANSWERING);
      if (!control || !region.contains(control)) out.push(node as Text);
      node = walker.nextNode();
    }
  }
  return out;
}

/**
 * Takes every mark off, leaving the text as the platform rendered it.
 *
 * Painting is not additive: the marks are re-applied from scratch after every
 * render, so removing one is a matter of leaving it out of the next coat. A
 * <mark> stripped here has its text handed back to its parent, and the parent
 * is normalised so the offsets are counted over the same nodes next time.
 */
export function unpaint(root: HTMLElement): void {
  for (const mark of [...root.querySelectorAll('mark.xmark')]) {
    const parent = mark.parentNode;
    if (!parent) continue;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    (parent as Element).normalize?.();
  }
}

/**
 * Where a point in the DOM falls in the container's plain text.
 *
 * Usually the point is in a text node that is counted. When it is not — a
 * selection begun inside an answer box, or anchored on an element, as a
 * triple-click is — it is placed at the next word of the paper after it, so
 * the candidate still gets the highlight they dragged out.
 */
export function offsetOf(root: Node, node: Node, offset: number): number | null {
  const nodes = textNodes(root);
  let total = 0;
  for (const text of nodes) {
    if (text === node) return total + offset;
    total += text.data.length;
  }
  if (nodes.length === 0) return null;

  const point = document.createRange();
  try {
    point.setStart(node, offset);
  } catch {
    return null;
  }
  point.collapse(true);
  total = 0;
  for (const text of nodes) {
    // At or after the point: the point falls just before this text.
    if (point.comparePoint(text, 0) >= 0) return total;
    total += text.data.length;
  }
  return total;
}

/** The selection as offsets into the container, or null if it is not inside it. */
export function selectionRange(root: HTMLElement): { start: number; end: number } | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;

  const start = offsetOf(root, range.startContainer, range.startOffset);
  const end = offsetOf(root, range.endContainer, range.endOffset);
  if (start === null || end === null || start === end) return null;
  return { start: Math.min(start, end), end: Math.max(start, end) };
}

/**
 * Paints the marks onto the container.
 *
 * Each mark is applied to every text node it covers, so a highlight may span
 * a bold word, a gap or a table cell without the markup being torn apart —
 * the same reason inputs are portalled into gaps rather than spliced in.
 */
export function paint(root: HTMLElement, marks: Mark[]): void {
  if (marks.length === 0) return;

  for (const mark of marks) {
    let cursor = 0;
    for (const text of textNodes(root)) {
      const start = cursor;
      const end = cursor + text.data.length;
      cursor = end;
      if (end <= mark.start || start >= mark.end) continue;
      if (text.parentElement?.closest('.xmark')) continue;

      const from = Math.max(0, mark.start - start);
      const to = Math.min(text.data.length, mark.end - start);
      if (to <= from) continue;

      const range = document.createRange();
      range.setStart(text, from);
      range.setEnd(text, to);

      const wrapper = document.createElement('mark');
      wrapper.className = mark.note ? 'xmark xmark--note' : 'xmark';
      wrapper.dataset.mark = mark.id;
      if (mark.note) wrapper.title = mark.note;
      try {
        range.surroundContents(wrapper);
      } catch {
        // A range that straddles an element boundary inside one text node
        // cannot happen; anything else is left unpainted rather than risking
        // the markup.
      }
      /*
       * On to the next node the mark covers, rather than stopping here.
       *
       * This used to break out after the first one, so a highlight drawn over
       * anything the markup interrupts — a bold word, two table cells, the end
       * of one paragraph and the start of the next — painted only as far as
       * the first interruption. The candidate selected a sentence and got half
       * of it, ending mid-phrase for no reason they could see.
       *
       * Wrapping one node does not disturb the others: each is a separate text
       * node, and surroundContents only splits the one it is given. The node
       * list was taken before this loop for the same reason.
       */
    }
  }
}

/** Marks that no longer touch anything, dropped when the text they sat on went. */
export const overlapping = (marks: Mark[], start: number, end: number): Mark[] =>
  marks.filter((m) => m.start < end && m.end > start);
